<!--
  File name: CrudTable.vue
  Program Description:
    - This component renders the table pages throughout the system.
    - It renders the assigned `table.js` file assigned in the feature that can
    be found inside the meta\mdTable\crudModules route
    - It also executes methods that allows each table form to create, view,
    edit or delete items in the page.
    - Functions found in the computed property returns the details passed from
    the specified table.js file.
    - The functions found in the method property handles all the actions being
    processed in the page, from deleting, resetting or filtering the entries,
    and populating the main table.

-->

<template>
  <v-container flex>
    <template>
      <!-- default theme here -->
      <v-row class="flex-grow-0 flex-shrink-0">
        <v-col>
          <v-card
            tile
            elevation="0"
          >
            <v-card-title :style="'background-color: #d0d3d9; padding: 0'">
              <div
                class="text-h4"
              >
                <b>{{ tableHeader }}</b>
              </div>
              <!-- <v-breadcrumbs
                :items="opts.breadcrumbs.items"
                :divider="opts.breadcrumbs.divider"
                :large="opts.breadcrumbs.large"
              /> -->
              <v-spacer />
              <template v-for="(item) in opts.tableActions">
                <template v-if="item.id === 'create'">
                  <v-btn
                    v-if="showLabel(item.privilege || `canCreate${roleName}`)"
                    :id="`${item.id}-btn-default`"
                    :key="`${item.id}-btn-default`"
                    v-bind="item.vBind"
                    text
                    :color="item.color ? item.color : 'green'"
                    :loading="loading"
                    @click="item.action ? item.action() : $router.push({ path: `${parentId}${collectionName}/create`})"
                  >
                    <v-icon class="mr-2">
                      {{ item.icon || 'mdi-plus-circle-outline' }}
                    </v-icon> {{ getLabel(item.privilege || `canCreate${roleName}`, item.label) }}
                  </v-btn>
                </template>
                <template
                  v-else-if="item.id === 'delete'"
                >
                  <v-btn
                    v-if="showLabel(item.privilege || `canDelete${roleName}`)"
                    :id="`${item.id}-btn-default`"
                    :key="`${item.id}-btn-default`"
                    text
                    color="red"
                    :loading="loading"
                    v-bind="item.vBind"
                    @click="item.action ? item.action() : deleteDialog(item.callback)"
                  >
                    <v-icon class="mr-2">
                      {{ item.icon || 'mdi-delete' }}
                    </v-icon> {{ getLabel(item.privilege || `canDelete${roleName}`, item.label) }}
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    v-if="showLabel(item.privilege)"
                    :id="`${item.id}Btn`"
                    :key="`${item.id}-btn-default`"
                    text
                    :color="item.color"
                    :loading="loading"
                    @click="item.action"
                  >
                    {{ item.privilege ? getLabel(item.privilege, item.label) : item.label }}
                  </v-btn>
                </template>
              </template>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="bulkDelete">
        <v-col class="pb-0">
          <v-card tile>
            <v-card-text class="d-flex flex-row-reverse pt-2 pb-2">
              <v-btn
                v-if="showLabel(`canDelete${roleName}`)"
                :id="`delete-btn-default`"
                :key="`delete-btn-default`"
                small
                outlined
                color="red"
                :loading="loading"
                :disabled="selectedLines.length === 0"
                @click="deleteDialog()"
              >
                <v-icon class="mr-2">
                  {{ 'mdi-delete' }}
                </v-icon> {{ getLabel(`canDelete${roleName}`, 'Delete') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="flex-grow-1">
        <v-col class="pt-0">
          <v-card tile>
            <v-card-text>
              <v-data-table
                v-model="selectedLines"
                :show-select="!hideSelect"
                :headers="headers"
                :items="opts.listFilter ? listFilter(list) : list"
                :options.sync="options"
                :server-items-length="total"
                :footer-props="footerProps"
                :loading="loading"
                item-key="_id"
                v-bind="vBind"
                @update:options="populateTable()"
                @toggle-select-all="disableSelect && selectAllToggle"
                @click:row="rowClick"
              >
                <template
                  v-if="disableSelect"
                  v-slot:[`item.data-table-select`]="{ item, isSelected, select }"
                >
                  <v-simple-checkbox
                    :ripple="false"
                    :value="isSelected"
                    :readonly="!item.isSelectable"
                    :disabled="!item.isSelectable"
                    @input="select($event)"
                  />
                </template>

                <template v-slot:top>
                  <v-text-field
                    v-if="showLabel(opts.privilege || `canViewAll${roleName}`)"
                    :prepend-icon="appendSearchIcon ? '' : 'search'"
                    :append-icon="appendSearchIcon? 'search' : ''"
                    :label="getLabel(opts.privilege || `canViewAll${roleName}`, 'Search')"
                    single-line
                    dense
                    :loading="loading"
                    :disabled="loading"
                    @input="val => {
                      if (val.length) return
                      searchWord = ''
                      resetTable()
                    }"
                    @keyup.enter="e => {
                      searchWord = e.target.value.trim()
                      resetTable()
                    }"
                  />
                </template>
                <template
                  v-if="multipleUpdate"
                  v-slot:[`item.active`]="{item}"
                >
                  <v-spacer />
                  <v-switch
                    v-model="item.active"
                    :disabled="!updateStatus"
                  />
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-spacer />
                  <template v-for="action in opts.entryActions">
                    <template v-if="action.id === 'view' || action.id === 'edit' || action.id === 'del'">
                      <v-btn
                        v-if="showLabel(action.privilege || `can${capitalize(action.id)}${roleName}`)"
                        :id="`${action.id}TableBtn`"
                        :key="action.id"
                        icon
                        :color="getIconColor({
                          role: (action.privilege || `can${capitalize(action.id)}${roleName}`),
                          successColor: action.color
                        })"
                        @click="action.action ? action.action(item) : $router.push({ path: `${parentId}${collectionName}/${action.id}/${item._id}` })"
                      >
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              {{ action.icon }}
                            </v-icon>
                          </template>
                          <span>{{ action.id === 'del' ? 'Delete' : capitalize(action.id) }} {{ collectionLabel }}</span>
                        </v-tooltip>
                      </v-btn>
                    </template>
                    <template v-else-if="action.id === 'delete'">
                      <v-btn
                        v-if="showLabel(action.privilege || `can${capitalize(action.id)}${roleName}`)"
                        id="deleteTableBtn"
                        :key="action.id"
                        icon
                        :color="getIconColor({
                          role: (action.privilege || `can${capitalize(action.id)}${roleName}`),
                          successColor: action.color
                        })"
                        @click="deleteItem"
                      >
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              {{ action.icon }}
                            </v-icon>
                          </template>
                          <span>{{ capitalize(action.id) }} {{ collectionLabel }}</span>
                        </v-tooltip>
                      </v-btn>
                    </template>
                    <template v-else>
                      <v-btn
                        v-if="showLabel(action.privilege || `can${capitalize(action.id)}${roleName}`)"
                        :id="`${action.id}TableBtn`"
                        :key="action.id"
                        icon
                        :color="getIconColor({
                          role: (action.privilege || `can${capitalize(action.id)}${roleName}`),
                          successColor: action.color
                        })"
                        @click="action.action()"
                      >
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              {{ action.icon }}
                            </v-icon>
                          </template>
                          <span>{{ capitalize(action.id) }} {{ collectionLabel }}</span>
                        </v-tooltip>
                      </v-btn>
                    </template>
                  </template>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import crud from '../utils/rest/crud'
import { VTextField, VRow, VCol } from 'vuetify/lib'
import { privilegeMixin } from 'session-plugin'
import { pluralize } from '../utils/helpers'
export default {
  name: 'CrudTable',
  components: {
    VTextField,
    VRow,
    VCol
  },
  mixins: [
    privilegeMixin
  ],
  props: {
    meta: {
      type: Function,
      default: () => 1
    },
    vuex: {
      type: Object,
      default: () => {}
    },
    itemSelected: {
      type: String,
      default: 'XXX'
    },
    bulkDelete: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    disabledCount: 0,
    selectedLines: [],
    searchWord: '',
    total: 0,
    list: [],
    loading: false,
    options: {
      page: 1,
      itemsPerPage: 10
    },
    footerProps: {
      'items-per-page-options': [5, 10, 25, 50]
    },
    formItem: {
    },
    file: null,
    importDialog: false,
    search: null,
    searchKey: null,
    updateStatus: false
  }),
  computed: {
    collectionName () {
      return this.opts.collectionName
    },
    roleName () {
      return this.capitalize(this.collectionName)
    },
    opts () {
      return this.$props.meta(this)
    },
    vBind () {
      return this.opts.vBind
    },
    dataView () {
      return this.opts.dataView
    },
    flatFields () {
      return this.flattenFields(this.opts.fields)
    },
    fields () {
      return this.flatFields.filter(x => x.properties && x.properties.key)
    },
    headers () {
      const { alignedStartActions, entryActions, status } = this.opts
      const columns = this.flatFields
        .filter(x => x.columnHeader)
        .map((x) => x.columnHeader)
        .filter((x) => {
          if (!x.state) { return x }
        })
      if (status) {
        columns.push(status[0].columnHeader)
      }
      if (entryActions) {
        const actions = {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          cellClass: this.collectionName === 'notificationType' ? 'px-1' : '',
          class: this.collectionName === 'notificationType' ? 'px-1' : '',
          align: alignedStartActions ? 'start' : 'end'
        }
        if (alignedStartActions) {
          columns.unshift(actions)
        } else {
          columns.push(actions)
        }
      }
      return columns
    },
    startAt () {
      return (this.options.page - 1) * this.options.itemsPerPage
    },
    filters () {
      const filter = this.query || {}
      if (this.searchKey && this.search) {
        filter[this.searchKey] = { $regex: this.search }
      }
      return filter
    },
    collectionLabel () {
      return this.opts.label
    },
    tableHeader () {
      return this.opts.tableHeader
    },
    lookupState () {
      return this.$store.state.lookups[this.collectionName]
    },
    hideSelect () {
      return this.opts.hideSelect
    },
    disableSelect () {
      return this.opts.disableItemSelect
    },
    disableItemFunction () {
      return this.opts.disableItemFunction
    },
    selectEnabledItems () {
      return this.selectedLines.length !== this.list.length - this.disabledCount
    },
    multipleUpdate () {
      return this.opts.multipleUpdate
    },
    updateObject () {
      return this.opts.updateObject
    },
    hideMenuDelete () {
      return this.opts.hideMenuDelete
    },
    parentId () {
      return this.$route.params.id ? `${this.$route.params.id}/` : ''
    },
    tableActions () {
      return this.opts.tableActions
    },
    appendSearchIcon () {
      return this.opts.appendSearchIcon
    },
    customDesignProps () {
      return this.opts.customDesign
    },
    isCheckItemsSelected () {
      return this.selectedLines.length > 0
    }
  },
  watch: {
    collectionName () {
      this.searchWord = ''
      this.populateTable()
    },
    query () {
      this.populateTable()
    },
    list (val) {
      this.disableSelect && (this.disabledCount = val.filter((i) => !i.isSelectable).length)
    },
    itemSelected () {
      this.searchWord = ''
    },
    'options.page': function () {
      this.resetSubTable()
    }
  },
  methods: {
    listFilter (list) {
      const filtered = this.opts.listFilter(list)
      return filtered
    },
    async rowClick (item, row) {
      await row.select(true)
      this.$emit('itemSelect', item)
    },
    api (action) {
      return crud(this.opts.route[`${action}`] || this.collectionName)
    },
    flattenFields (fields) {
      return fields.flatMap((x) => {
        let v = [x]
        if (x.children) {
          v = v.concat(this.flattenFields(x.children))
        }
        return v
      })
    },
    capitalize (value) {
      if (value) return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
      return ''
    },

    async updateMultiple () {
      const resp = await this.api('updateMultiple').updateMultiple({ info: this.updateObject })
      const data = await resp.json()
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: `${this.collectionLabel} updated successfully`,
          type: 'success'
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: data.error || data.message,
          type: 'error'
        })
      }
      this.updateStatus = false
    },
    selectAllToggle (value) {
      this.selectedLines = []
      if (this.selectEnabledItems) {
        value.items.forEach(item => item.isSelectable && this.selectedLines.push(item))
      }
    },
    async populateTable () {
      this.loading = true
      this.selectedLines = []
      const { itemsPerPage, sortBy, sortDesc } = this.options
      const data = await this.api('list').list({
        filter: this.filters,
        start: this.startAt,
        count: itemsPerPage,
        search: this.searchWord,
        dataview: this.dataView,
        advancedQuery: this.opts.advancedQuery,
        extQuery: (sortBy && sortBy[0]) ? `sortBy=${sortBy[0]}${sortDesc[0] ? '&asc=-1' : ''}` : ''
      })
      this.list = this.disableSelect ? this.disableItemFunction(data.entries) : data.entries
      this.total = data.total
      this.loading = false
    },
    resetTable () {
      this.options.page = 1
      this.populateTable()
      this.resetSubTable()
    },
    resetSubTable () {
      this.$emit('resetSubTable')
    },
    async deleteItems (callback) {
      this.loading = true
      const ids = this.selectedLines.map(selected => selected._id)
      const resp = await this.api('deleteMultiple').deleteMultiple(ids)
      const { error, entry, errors } = await resp.json()
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: `${pluralize(this, entry.deletedCount, this.collectionLabel)} successfully deleted`,
          type: 'success'
        })
      } else {
        const load = errors.length ? { list: errors } : { message: error }
        this.$store.dispatch('showAlert', { type: 'error', ...load })
      }
      this.loading = false
      this.populateTable()
    },
    upload () {
      return this.api('upload').upload({ file: this.file })
    },
    deleteDialog (callback = () => {}) {
      if (this.selectedLines.length === 0) {
        this.$store.dispatch('showDialog', {
          removeCancel: true,
          title: `Select ${this.collectionLabel} to be deleted`,
          buttonLabel: 'OK',
          buttonColor: 'black'
        })
      } else {
        const label = pluralize(this, this.selectedLines.length, this.collectionLabel)
        this.$store.dispatch('showDialog', {
          title: `Delete ${label}`,
          message: `Are you sure you want to delete the selected ${label}? This action cannot be undone.`,
          buttonLabel: 'Delete',
          callback: () => { this.deleteItems(callback) }
        })
      }
    },
    customDesign (option) {
      return this.opts.customDesign && this.opts.customDesign.name === option
    }
  }
}
</script>

<style scoped>
  /* .v-breadcrumbs {
    padding-top: 0;
    padding-bottom: 0;
  } */
</style>
